import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api, type PendingReviewItem } from '../lib/api';
import { formatBytes, formatDate, formatDateTime } from '../lib/format';

/**
 * Everything waiting on a human, across the fleet.
 *
 * This is the screen a safety manager opens first thing: drivers upload overnight, and
 * nothing they send counts until someone here looks at it.
 */
export function ReviewQueue() {
  const queryClient = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const { data, isPending, error } = useQuery({
    queryKey: ['review-queue'],
    queryFn: () => api.get<{ pending: PendingReviewItem[] }>('/api/drivers/review-queue'),
  });

  const review = useMutation({
    mutationFn: ({ id, ...body }: { id: string; decision: 'accept' | 'reject'; reason?: string }) =>
      api.post(`/api/drivers/artifacts/${id}/review`, body),
    onSuccess: () => {
      setRejectingId(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      void queryClient.invalidateQueries({ queryKey: ['driver'] });
    },
  });

  if (isPending) return <p className="muted">Loading…</p>;
  if (error) return <p className="field-error">{(error as Error).message}</p>;

  const pending = data?.pending ?? [];

  return (
    <div className="fleet">
      <header className="fleet-head">
        <p className="eyebrow">Submissions</p>
        <h1 className="fleet-title">Review queue</h1>
        <p className="ledger">
          {pending.length === 0 ? (
            <>Nothing is waiting. Every submission has been reviewed.</>
          ) : (
            <>
              <span className="ledger-num tone-pending tnum">{pending.length}</span>{' '}
              {pending.length === 1 ? 'submission is' : 'submissions are'} waiting. Nothing counts
              toward a driver's file until you accept it.
            </>
          )}
        </p>
      </header>

      {pending.length > 0 && (
        <section className="card">
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th scope="col">Driver</th>
                  <th scope="col">Item</th>
                  <th scope="col">Dates given</th>
                  <th scope="col">Submitted</th>
                  <th scope="col">Decision</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/drivers/${p.driver_id}`} className="driver-link">
                        {p.first_name} {p.last_name}
                      </Link>
                    </td>
                    <td>
                      <span className="headline-title">
                        {p.requirement_code.replaceAll('_', ' ').toLowerCase()}
                      </span>
                      {p.sensitivity === 'restricted' && (
                        <span className="check-flag is-restricted">restricted</span>
                      )}
                      <span className="driver-id cite">
                        {p.mime_type ?? p.artifact_kind} · {formatBytes(p.byte_size)}
                      </span>
                    </td>
                    <td className="tnum">
                      {formatDate(p.effective_at)}
                      {p.expires_at && (
                        <span className="due-rel muted">expires {formatDate(p.expires_at)}</span>
                      )}
                    </td>
                    <td className="tnum muted">{formatDateTime(p.uploaded_at)}</td>
                    <td>
                      <div className="row">
                        <a
                          className="btn btn-secondary btn-sm"
                          href={`/api/documents/${p.id}/download`}
                        >
                          Open
                        </a>
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={review.isPending}
                          onClick={() => review.mutate({ id: p.id, decision: 'accept' })}
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setRejectingId(rejectingId === p.id ? null : p.id)}
                        >
                          Reject
                        </button>
                      </div>

                      {rejectingId === p.id && (
                        <div className="check-reject">
                          <label htmlFor={`rq-reason-${p.id}`}>
                            What does the driver need to send instead?
                          </label>
                          <textarea
                            id={`rq-reason-${p.id}`}
                            rows={2}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                          />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            disabled={reason.trim().length === 0 || review.isPending}
                            onClick={() =>
                              review.mutate({ id: p.id, decision: 'reject', reason })
                            }
                          >
                            Send back to driver
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {review.error && (
            <p className="field-error" style={{ padding: '0 1.5rem 1rem' }}>
              {(review.error as Error).message}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
